import "src/styles/globals.css";
import "src/styles/markdown.css";
import "src/styles/ir-page.css";
import React, {
  PropsWithChildren,
  ReactNode,
  Suspense,
  useEffect,
  useRef,
  useState,
} from "react";
import { useRouter } from "next/router";
import Script from "next/script";
import { analyticsInstance, pageview, GtagScript } from "@/libs/analytics";
import { Env, isProd } from "src/Env";
import Spinning from "src/components/animations/Spinning";
import {
  HydrationBoundary,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import Toast from "src/components/Toast";
import { hotjar } from "react-hotjar";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { AppProps } from "next/app";
import { appWithTranslation } from "next-i18next";
import nextI18NextConfig from "../../next-i18next.config.js";
import Header from "@/components/Header";
import { Noto_Sans } from "next/font/google";
import { useMount } from "react-use";
import FbPixel from "@/utils/FbPixel";
import { GlobalRouterLoading } from "@/components/GlobalRouterLoading";
import { GlobalEScripts } from "@/components/GlobalEScripts";
import { GeConsumedCartWatcher } from "@/components/GeConsumedCartWatcher";
import { shouldClaimInsteadOfMerge } from "@/libs/global-e/checkout-marker";
import { CartSheet } from "@/v2-components/cart-sheet/index.tsx";
import WelcomeCouponBadge from "@/v2-components/welcome-coupon-badge";
import { Halloween2026Cursor } from "@/v2-components/event/halloween-2026-cursor";
import { OverweightShippingDialog } from "@/v2-components/overweight-shipping-dialog";
import useUser from "@/api/apis";
import { claimGuestCartOwner, mergeGuestCart, syncCart } from "@/api/CartApi";
import { getCartState, useNormalizeCartSets } from "@/store/cart-store";
import { hydrateLocaleStore, useLocale } from "@/store/locale-store.ts";
import { useLocaleReadyAttribute } from "@/libs/locale/locale-ready";
import { useStripGlCountryParam } from "@/hooks/useStripGlCountryParam";
import SelectLocaleDialog from "@/components/SelectLocale.tsx";
import {
  ConsentBanner,
  useConsentStore,
  ConsentCategory,
} from "@/libs/consent";

declare global {
  interface Window {
    wcs?: { inflow: (domain: string) => void };
    wcs_do?: () => void;
  }
}

const notoSans = Noto_Sans({
  subsets: ["latin"],
  weight: ["400", "500", "600"],
  display: "swap",
});

function Loading() {
  return (
    <div className="flex h-screen w-screen items-center justify-center">
      <Spinning className="h-10 w-10" />
    </div>
  );
}

function App({ Component, pageProps }: AppProps & { Component: any }) {
  const getLayout =
    Component.getLayout ??
    ((page: ReactNode) => (
      <DefaultAppLayout pageProps={pageProps}>{page}</DefaultAppLayout>
    ));
  return getLayout(<Component {...pageProps} />);
}

// TODO
// SuggestLocale 을 _app 쪽으로 옮기기
// 페이지마다 UI 요구 사항이 달라서 일단 보류중입니다.
function DefaultAppLayout({
  children,
  pageProps,
}: PropsWithChildren<{ pageProps: AppProps["pageProps"] }>) {
  const router = useRouter();
  // iframe 임베드 모드: ?embed=1 인 경우 ConsentBanner 등
  // 호스트 페이지에 이미 떠 있어야 하는 chrome 을 임베드 페이지에서는 숨긴다.
  const isEmbed = router.query.embed === "1";
  const hasConsented = useConsentStore((s) => s.hasConsented);
  const performanceAllowed = useConsentStore(
    (s) => s.consent[ConsentCategory.Performance],
  );
  const advertisingAllowed = useConsentStore(
    (s) => s.consent[ConsentCategory.Advertising],
  );

  const hotjarInitialized = useRef(false);

  // Instead do this, which ensures each request has its own cache:
  const [queryClient] = useState(() => {
    return new QueryClient({
      defaultOptions: {
        queries: {
          retry: 1,
          refetchOnWindowFocus: false,
          staleTime: 1000 * 60 * 5,
        },
      },
    });
  });

  useEffect(() => {
    if (isProd) {
      console.log = () => {};
      console.error = () => {};
      console.warn = () => {};
      console.info = () => {};
    }
  }, []);

  useEffect(() => {
    analyticsInstance?.init();
  }, []);

  // localeStore 의 countryCode 는 SSR-safe 하게 빈 문자열로 초기화돼 있다.
  // 마운트 후 쿠키에서 읽어 hydrate 해야 hydration mismatch 가 나지 않는다.
  useEffect(() => {
    hydrateLocaleStore();
  }, []);

  // Hotjar — only with performance consent, initialize once
  useEffect(() => {
    if (
      isProd &&
      hasConsented &&
      performanceAllowed &&
      !hotjarInitialized.current
    ) {
      hotjar.initialize({ id: 3799543, sv: 6 });
      hotjarInitialized.current = true;
    }
  }, [hasConsented, performanceAllowed]);

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      if (hasConsented && advertisingAllowed) {
        FbPixel.pageView();
      }
      if (hasConsented && performanceAllowed) {
        pageview(url);
      }
    };

    router.events.on("routeChangeComplete", handleRouteChange);
    router.events.on("hashChangeComplete", handleRouteChange);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
      router.events.off("hashChangeComplete", handleRouteChange);
    };
  }, [router.events, hasConsented, advertisingAllowed, performanceAllowed]);

  /**
   * iOS에서 input 요소에 포커스가 가면 화면이 줌인되는 현상을 방지하기 위해
   */
  useMount(() => {
    if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
      const meta = document.createElement("meta");
      meta.name = "viewport";
      meta.content =
        "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=yes";
      document.head.appendChild(meta);
    }
  });

  return (
    <>
      {/* 네이버 공통 유입경로 스크립트 — 종전 _document head 동기 로드(전 페이지 렌더 차단)를
          afterInteractive 로 전환. wcs_add 설정은 _document 인라인이 선행하며,
          유입 계측(inflow/wcs_do)은 스크립트 로드 완료 후 여기서 수행 */}
      <Script
        id="naver-wcslog"
        src="https://wcs.naver.net/wcslog.js"
        strategy="afterInteractive"
        onReady={() => {
          if (window.wcs) {
            window.wcs.inflow("bhaptics.com");
          }
          if (typeof window.wcs_do === "function") {
            window.wcs_do();
          }
        }}
      />
      {/* Facebook Pixel — only with advertising consent */}
      {hasConsented && advertisingAllowed && (
        <Script
          id="fb-pixel"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', ${Env.FacebookPixelId});
          `,
          }}
        />
      )}
      <Suspense fallback={<Loading />}>
        <Header seo={pageProps?.seo} />
        <GtagScript />
        {/* Microsoft Clarity — only with performance consent */}
        {isProd && hasConsented && performanceAllowed && (
          <Script
            id="clarity-init"
            strategy="lazyOnload"
            dangerouslySetInnerHTML={{
              __html: `
            (function(c,l,a,r,i,t,y){
              c[a] = c[a] || function(){
                (c[a].q = c[a].q || []).push(arguments)
              };
              t = l.createElement(r);
              t.async = 1;
              t.src = "https://www.clarity.ms/tag/" + i;
              y = l.getElementsByTagName(r)[0];
              y.parentNode.insertBefore(t, y);
            })(window, document, "clarity", "script", "st362luqvp");
          `,
            }}
          />
        )}
        <QueryClientProvider client={queryClient}>
          <HydrationBoundary state={pageProps?.dehydratedState}>
            <Toast />
            <main className={notoSans.className + " text-coolgray-90"}>
              {children}
            </main>
          </HydrationBoundary>
          <AvoidHydration />
          <CartLoginSync />
          {/* 담긴 뒤 가격·재고가 바뀐 수량 세트(MOLLE Pouch ×2)를 화면에 보일 때 다시 정리 — #5906 */}
          <CartSetNormalizer />
          {/* GE 주문으로 소비된 카트 회수(리다이렉트 결제 복귀 등) — docs/global-e-storefront.md §4.7.3 */}
          <GeConsumedCartWatcher />
          <CartSheet />
          <ReactQueryDevtools />
          <GlobalRouterLoading />
          {!isEmbed && <ConsentBanner />}
          {/* 신규 가입 쿠폰 미니 배지 — 팝업(메인 전용)이 안 보이는 화면에서 가입 유도 접점을
              유지한다. 결제 화면 제외는 컴포넌트 안에서 라우트로 판정. React Query 를 쓰므로
              반드시 QueryClientProvider 안쪽이어야 한다
              (docs/pitfalls.md #1(Header 는 QueryClientProvider 밖에서 렌더된다)). */}
          {!isEmbed && <WelcomeCouponBadge />}
          {/* 할로윈 2026 커서 컴패니언 — 기간 중 · 데스크톱 마우스만 · 결제 화면 제외(#6061) */}
          {!isEmbed && <Halloween2026Cursor />}
          <GlobalEScripts />
        </QueryClientProvider>
      </Suspense>
    </>
  );
}

// 담긴 뒤 가격·재고가 바뀐 수량 세트를 다시 정리 — 근거는 `useNormalizeCartSets` docblock
function CartSetNormalizer() {
  useNormalizeCartSets();
  return null;
}

// 로그인 시 localStorage 장바구니를 서버에 동기화
function CartLoginSync() {
  const { user } = useUser();
  const router = useRouter();

  useEffect(() => {
    if (!user?.isLoggedIn) return;
    // 경로에 따라 **둘 중 하나만** 실행 — 두 동작은 같은 guest-cart 문서(bh_cart_token)를 락 없이
    // read-modify-write 하므로 한 페이지에서 함께 발사하면 서로 덮어쓴다(§2.5-4 lost-update).
    //
    // ★「GE 페이지」만이 아니라 **GE 결제 진행 중**이면 claim 쪽이다(`shouldClaimInsteadOfMerge`). 결제가 GE
    // 페이지 밖에서 끝나면 SendOrder 는 그 **뒤에** 온다 — 그 착지에서 merge 가 돌면 claim 해 둔 문서가 지워져
    // 회원 주문이 게스트로 저장된다(2026-09-29 dev 실측 — GE 복귀 주소 설정 문제로 PayPal 이 사이트 루트로 돌아왔다,
    // 10-01 GE 수정. docs/global-e-storefront.md §4.7.3). pathname 게이트가 "GE 흐름은 GE 페이지 안에서 끝난다"를 전제했던 것이 깨진 지점이다.
    if (shouldClaimInsteadOfMerge(router.pathname)) {
      // GE 국제 체크아웃 페이지(또는 결제 진행 창): **claim 만**.
      // - claim: cart token 소유자(ownerUid) 기록 → GE 주문 회원 귀속(§2.5, mgmt #1179). 이 페이지에서
      //   부르는 claim 이 **authoritative** — 진입 직전 buy-now/cart 흐름이 guest-cart 를 확정
      //   (writeGuestCartForCheckout/flushCartSync)해 둔 그 토큰에, 경쟁 write 없이 소유자만 얹는다.
      // - merge·sync 는 **금지**: 둘 다 로그인 유저의 쇼핑 카트를 guest-cart 로 되돌려 덮어써 GE 가
      //   잘못된 수량을 읽는다(§4.4 buy-now 수량 버그). merge 는 게스트 문서 삭제까지(§2.5).
      // (full-nav 진입 = 재마운트라 마운트 시점 pathname 정확. flag OFF 인 prod 은 이 경로 미도달 →
      //  claim 이 사실상 flag-gated = prod 무영향.)
      claimGuestCartOwner();
    } else {
      // 그 외 모든 페이지: 기존 동작 그대로 (로그인 시 게스트 카트를 uid 카트로 채택 + 로컬 카트 sync).
      // claim 은 여기서 안 부른다 — merge 가 곧 게스트 문서를 삭제하므로 무의미하고, sync mirror 와
      // 레이스만 만든다. 회원 귀속은 체크아웃 페이지의 claim 이 담당(위).
      mergeGuestCart();
      const items = getCartState().items;
      if (items.length > 0) {
        syncCart(items.map((i) => ({ sku: i.sku, quantity: i.quantity })));
      }
    }
    // router.pathname 은 마운트 시점 값만 사용(의도) — 로그인 전이 시 1회 실행.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.isLoggedIn]);

  return null;
}

const AvoidHydration = () => {
  const {
    languageCode,
    dialog,
    setDialog,
    countryCode,
    setLocale,
    isCountryHydrated,
  } = useLocale();

  // 국가가 확정되면 첫 화면의 숨김(`data-locale-pending`)을 푼다 — src/libs/locale/locale-ready.ts
  useLocaleReadyAttribute(isCountryHydrated);

  // 피드 링크의 `?glCountry=` 를 주소에서 지운다 — 남으면 국가 선택이 새로고침에서 되돌아간다(#5858).
  useStripGlCountryParam();

  return (
    <>
      {/* 배송 상한 초과 안내(바로구매) — 호출처마다 두면 lazy mount 에서 silent fail 하므로
          앱에서 한 번만 렌더한다(docs/pitfalls.md "Dialog 내부 useState"). */}
      <OverweightShippingDialog />
      {(dialog === "select-locale-dialog" ||
        dialog === "select-geo-based-locale-dialog" ||
        dialog === "non-shippable-dialog") && (
        <SelectLocaleDialog
          countryCode={countryCode}
          languageCode={languageCode}
          nonShippable={dialog === "non-shippable-dialog"}
          onOpenChange={(open) => {
            if (!open) {
              setDialog(null);
            }
          }}
          onSave={(countryCode, languageCode) => {
            setLocale({
              countryCode,
              languageCode,
            });
            setDialog(null);
          }}
        />
      )}
    </>
  );
};

export default appWithTranslation(App, nextI18NextConfig as any);
